import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRightSmall } from '@peacecover/icon';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import { CastRelationshipBadge } from './CastRelationshipBadge';
import { VirtuosoTouchPanScroller } from '../../../components/virtualized/VirtuosoTouchPanScroller';
import EmptyCase from '../../../components/errors/EmptyCase';
import { ROUTES } from '../../../constants/routes';
import { SCENE_QUERY_KEYS } from '../../../domains/scene/ui/queryKeys';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];

const PreviewSubscriptionList = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useGetSceneSubscriptions('SceneEpisode');

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneSubscriptions('SceneEpisode'));
    };
  }, [queryClient]);

  const previewList = data?.pages?.flatMap((page) => page.userSceneSubscriptions) ?? [];

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  if (data?.pages?.length && previewList?.length === 0) {
    return (
      // @ts-ignore
      <EmptyCase emoji="😳" title={t('SceneMain.subscription.emptyTitle')} />
    );
  }

  return (
    <div className="h-full min-h-0 overflow-x-hidden touch-pan-y overscroll-y-contain">
      <Virtuoso
        components={{ Scroller: VirtuosoTouchPanScroller }}
        overscan={480}
        defaultItemHeight={260}
        style={{ height: '100%' }}
        data={previewList}
        endReached={loadMore}
        atBottomThreshold={200}
        itemContent={(_index, episode: SceneSubscription) => (
          <SceneOriginalEpisodeItem episode={episode} />
        )}
      />
    </div>
  );
};

export default PreviewSubscriptionList;

const SceneOriginalEpisodeItem = ({ episode }: { episode: SceneSubscription }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleNavigateToEpisode = () => {
    const baseParams = new URLSearchParams();
    if (episode.locator?.sceneEpisodeId)
      baseParams.set('episodeId', episode.locator.sceneEpisodeId);
    if (episode.locator?.paragraphIndex != null)
      baseParams.set('cursor', String(episode.locator.paragraphIndex));
    if (episode.locator?.charOffset != null)
      baseParams.set('charOffset', String(episode.locator.charOffset));

    const queryString = baseParams.toString();
    navigate(
      `${ROUTES.SCENE.ORIGINAL_EPISODES.GO(episode.sceneId ?? '')}${queryString ? `?${queryString}` : ''}`
    );
  };

  const currentProgression = Math.floor((episode.progression ?? 0) * 100);
  const isSceneCompleted = episode.sceneStatus === 'completed';

  return (
    <div className="mx-[24px] py-[20px] flex items-center justify-between border-b border-separator-dark-2">
      <div className="flex items-center gap-[14px]">
        {episode.pictureUrl ? (
          <img
            src={episode.pictureUrl ?? '/assets/scene/scene_empty_thumbnail.png'}
            alt={episode.title ?? 'empty thumbnail'}
            className="w-[69px] h-[109px] rounded-[5px] border border-pure-black"
          />
        ) : (
          <div className="w-[69px] h-[109px]" />
        )}
        <div className="flex flex-col items-start gap-[8px]">
          <p className="text-[19px] font-semibold leading-[23px] text-[#FFF] max-w-[180px] truncate">
            {episode.title}
          </p>
          <p className="text-[12px] font-medium leading-[14px] text-[rgba(255,255,255,0.80)]">
            by {episode.author} · {Number(episode.totalCharCount).toLocaleString()}자
          </p>
          <div className="flex items-center gap-[8px]">
            <div className="w-[145px] h-[6px] bg-white-opacity-200 rounded-[20px]">
              <div
                className="h-[6px] bg-[linear-gradient(90deg,#41F7A2_0%,#5C3DFA_100%)] rounded-[20px]"
                style={{ width: `${currentProgression}%` }}
              />
            </div>
            <p className="text-[12px] font-medium leading-[14px] text-[rgba(255,255,255,0.80)]">
              {currentProgression}%
            </p>
          </div>
          {episode.castRelationship && (
            <CastRelationshipBadge
              castRelationship={episode.castRelationship}
              className="!text-main-dark-2 !bg-white-opacity-200"
            />
          )}
        </div>
      </div>
      {isSceneCompleted ? (
        <button type="button" onClick={handleNavigateToEpisode}>
          <ArrowRightSmall className="text-pure-white" />
        </button>
      ) : (
        <div className="px-[7px] py-[4px] flex items-center gap-[6px] rounded-[30px] bg-secondary-dark">
          <span
            className="w-[3px] h-[3px] rounded-full bg-onSecondary-dark"
            style={{ animation: 'fadeInOut 2s ease-in-out infinite' }}
          />
          <span className="text-[11px] font-semibold leading-[13px] text-onSecondary-dark">
            {t('SceneMain.subscription.inProgress')}
          </span>
        </div>
      )}
    </div>
  );
};
